import React from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from 'wtb-components';
import { Block, Divider } from 'components';
import { PAYMENT_TYPES } from 'constants.js';
import {
  useAutogiroContext,
  useBetalingsserviceContext,
  useBillingContext,
  useTransactionContext
} from 'context';
import { maskBankNumber } from 'utils';
import styles from './PrintYourInformation.module.css';
import { Logger } from 'wtb-utils';

const SHOW_BILLING_PLANS = [ PAYMENT_TYPES.INVOICE, PAYMENT_TYPES.AUTOGIRO, PAYMENT_TYPES.BETALINGSSERVICE ];

const ContactInfo = () => {
  const { t: translate } = useTranslation();

  const { customerData } = useTransactionContext();

  const hasContactInfo = (
    customerData?.firstName
    || customerData?.phone
    || customerData?.email
    || customerData?.nationalId
  );

  return hasContactInfo ? (
    <>
      <div className={styles.boxBorder}>
        <div className={styles.headerBorder}>
          <Typography variant="body2" component="div" fontWeight="bold">
            {translate('customerData.contactInfo')}
          </Typography>
        </div>

        <div className={styles.customerInfo}>
          <Typography variant="body2" component="div">
            {customerData.firstName} {customerData.lastName}
          </Typography>
          <Typography variant="body2" component="div">
            {customerData.nationalId}
          </Typography>
          <Typography variant="body2" component="div">
            {customerData.email}
          </Typography>
          <Typography variant="body2" component="div">
            {customerData.phone}
          </Typography>
        </div>
      </div>

      <Divider size="mid" />
    </>
  ) : null;
};

const AddressInfo = () => {
  const { t: translate } = useTranslation();

  const { customerData } = useTransactionContext();

  return customerData?.street ? (
    <>
      <div className={styles.boxBorder}>
        <div className={styles.addressBorder}>
          <Typography variant="body2" component="div" fontWeight="bold">
            {translate('customerData.shippingAddress')}
          </Typography>
        </div>

        <div className={styles.shippingInfo}>
          <Typography variant="body2" component="div">
            {customerData.street}
          </Typography>
          {customerData.streetAdditional && (
            <Typography variant="body2" component="div">
              {customerData.streetAdditional}
            </Typography>
          )}
          <Typography variant="body2" component="div">
            {customerData.zipCode} {customerData.city}
          </Typography>
          <Typography variant="body2" component="div">
            {customerData.country}
          </Typography>
        </div>
      </div>

      <Divider size="mid" />
    </>
  ) : null;
};

const BillingInfo = () => {
  const { t: translate } = useTranslation();

  const { bankInfo: autogiroBankInfo } = useAutogiroContext();
  const { bankInfo: betalingsserviceBankInfo } = useBetalingsserviceContext();
  const { billing } = useBillingContext();
  const { selectedPaymentPlan } = useTransactionContext();

  const getPaymentType = () => {
    switch (selectedPaymentPlan?.type) {
      case PAYMENT_TYPES.AUTOGIRO:
        return translate('paymentPlans.autogiro.title');
      case PAYMENT_TYPES.BETALINGSSERVICE:
        return translate('paymentPlans.betalingsservice.title');
      case PAYMENT_TYPES.SWISH:
        return translate('paymentPlans.swish.title');
      case PAYMENT_TYPES.AVTALEGIRO:
      case PAYMENT_TYPES.AVTALEGIRO_MOCK:
        return translate('paymentPlans.avtalegiro.title');
      case PAYMENT_TYPES.INVOICE:
        return translate('paymentPlans.invoice.title');
      case PAYMENT_TYPES.SEPA:
        return translate('paymentPlans.sepa.title');
      default:
        return translate('paymentPlans.card.title');
    }
  };

  const showBilling = SHOW_BILLING_PLANS.includes(selectedPaymentPlan?.type);
  const isAvtalegiro = [ PAYMENT_TYPES.AVTALEGIRO, PAYMENT_TYPES.AVTALEGIRO_MOCK ].includes(selectedPaymentPlan?.type);

  let bankInfo;
  switch (selectedPaymentPlan?.type) {
    case PAYMENT_TYPES.AUTOGIRO:
      bankInfo = autogiroBankInfo;
      break;
    case PAYMENT_TYPES.BETALINGSSERVICE:
      bankInfo = betalingsserviceBankInfo;
      break;
    default:
      bankInfo = null;
      if (selectedPaymentPlan?.type && !Object.values(PAYMENT_TYPES).includes(selectedPaymentPlan.type)) {
        Logger.warn('[BILLINGINFO] bankInfo not found for payment type:', selectedPaymentPlan.type);
      }
      break;
  }

  return (
    <div className={styles.lastBoxBorder}>
      <div className={styles.addressBorder}>
        <Typography variant="body2" component="div" fontWeight="bold">
          {translate('yourInformation.paymentMethod')}
        </Typography>
      </div>

      <div className={styles.shippingInfo}>
        <Typography variant="body2" component="div">
          {getPaymentType()}
        </Typography>

        {bankInfo?.accountNumber && (
          <>
            <Typography variant="body2" component="div" fontWeight="bold" className={styles.billingInfo}>
              {translate('customerInfo.accountNumber')}
            </Typography>
            <Typography variant="body2" component="div">
              {maskBankNumber(bankInfo?.accountNumber)}
            </Typography>
          </>
        )}

        {isAvtalegiro && (
          <Typography className={styles.avtalegiroText} variant="body2" color="textSecondary">
            {translate('orderSummary.yourInformation.avtalegiroText')}
          </Typography>
        )}

        {showBilling && billing && (
          <>
            <Typography variant="body2" component="div" fontWeight="bold" className={styles.billingInfo}>
              {translate('customerInfo.billingInfo')}
            </Typography>

            <Typography variant="body2" component="div">
              {`${billing.firstName} ${billing.lastName}`}
            </Typography>
            <Typography variant="body2" component="div">
              {billing.street}
            </Typography>
            {billing.streetAdditional && (
              <Typography variant="body2" component="div">
                {billing.streetAdditional}
              </Typography>
            )}
            <Typography variant="body2" component="div">
              {billing.zipCode} {billing.city}
            </Typography>
          </>
        )}
      </div>
    </div>
  );
};

export default function PrintYourInformation() {
  const { t: translate } = useTranslation();

  return (
    <div className={styles.root}>
      <Block
        className={styles.paddedBlock}
        title={translate('yourInformation.title')}
        collapsable={false}
        main
      >
        <ContactInfo />

        <AddressInfo />

        <BillingInfo />
      </Block>
    </div>
  );
}
